/**
 * aura-decay.ts — WEB.4.UI.9c
 *
 * PassStart aura auto-decay: when a player passes the Start tile (rolls
 * past tile 32 wrapping to 0), their owned auras with discardMode="PassStart"
 * are auto-discarded. The 3 live PassStart cards are Steam_Engine_Breakthrough,
 * Ocean_Acidification, and Drought.
 *
 * Whenever-mode auras do NOT auto-decay (user-confirmed 2026-05-12: "aura
 * will active until discard only"). They stay until the OWNER manually
 * discards them via the AuraPanel click-to-discard UI; see
 * manualDiscardAura action in game-store.ts.
 *
 * Teleport landings explicitly do NOT trigger PassStart decay — they're
 * not physical movement across the start line. See confirmTilePick for the
 * exclusion (no call site there).
 */

import type { AuraManagerState } from "@gdg/rules-engine";
import { discardAura } from "@gdg/rules-engine";
import type { ActionLog } from "@gdg/rules-engine";
import type { CardDef } from "@gdg/shared";

/**
 * Determine whether a roll caused the player to pass the Start tile.
 * Start = tile 0. Passing-start = wrap-around from high tile → low tile.
 *
 * Convention: `passingStart` true when `prevTile + roll >= TILE_COUNT`.
 * Landing exactly on Start (when prevTile == 0 and roll == 0 is impossible —
 * roll is 1..6) is treated as having passed Start.
 */
export function didPassStart(prevTile: number, roll: number, tileCount: number): boolean {
  return prevTile + roll >= tileCount;
}

/**
 * Walk auraState and discard all auras whose:
 *   - ownerSeat === seat (does NOT include global -1)
 *   - sourceCardName resolves to a CardDef with discardMode = "PassStart"
 *
 * Returns the new auraState + log + count of auras discarded.
 *
 * Discard-mode semantics (user-confirmed 2026-05-12):
 *   - "PassStart" — auto-decays on passing Start. Affects Steam_Engine_Breakthrough,
 *     Ocean_Acidification, Drought (3 cards in cards.json).
 *   - "Whenever" — stays active UNTIL MANUALLY discarded by owner via the AuraPanel
 *     click-to-discard UI. Does NOT auto-decay on pass-start. (Earlier UI.9c
 *     conflated these and incorrectly auto-decayed Whenever auras — user clarified.)
 *   - "Permanent" — never decays
 *   - "Instantly" / "AfterUse" — discarded at play / after-use time; should not be
 *     long-lived in auraState
 */
const PASSING_START_DISCARD_MODES = new Set(["PassStart"]);

export function decayPassingStartAuras(
  auraState: AuraManagerState,
  log: ActionLog,
  seat: number,
  cardLookup: (cardName: string) => CardDef | undefined,
): { newAura: AuraManagerState; log: ActionLog; discardedCount: number } {
  const seatBucket = auraState.bySeat[String(seat)] ?? [];

  // Walk a copy because each discard mutates auraState; collect IDs first.
  const toDiscard: number[] = [];
  for (const aura of seatBucket) {
    const card = cardLookup(aura.sourceCardName);
    if (card === undefined) continue;
    if (!PASSING_START_DISCARD_MODES.has(card.discardMode)) continue;
    toDiscard.push(aura.instanceId);
  }

  let currentAura = auraState;
  let currentLog = log;
  let discardedCount = 0;
  for (const instanceId of toDiscard) {
    const res = discardAura(currentAura, currentLog, instanceId);
    if (res.removed) {
      currentAura = res.newState;
      currentLog = res.log;
      discardedCount++;
    }
  }
  return { newAura: currentAura, log: currentLog, discardedCount };
}
